/**
 * Configuration for the institutional stamp displayed on deduction forms.
 */
export interface StampConfig {
    /** Shape of the stamp */
    shape?: 'circle' | 'rectangle' | 'square';
    /** Main color (hex or CSS color) */
    color?: string;
    /** Short organization name shown prominently (e.g. 'HAZINA') */
    orgName?: string;
    /** Text on the top arc/band */
    topText?: string;
    /** Text on the bottom arc/band */
    bottomText?: string;
    /** First center line (e.g. P.O.Box address) */
    centerLine1?: string;
    /** Second center line (e.g. city) */
    centerLine2?: string;
    /**
     * Optional URL/path to a stamp image to display instead of the SVG drawn stamp.
     * When set, the image is rendered in place of the SVG stamp.
     * e.g. '/images/hazina_stamp.svg' or 'https://cdn.example.com/stamp.png'
     */
    stampImageUrl?: string;
}

export const DEFAULT_STAMP_CONFIG: Required<Omit<StampConfig, 'stampImageUrl'>> & Pick<StampConfig, 'stampImageUrl'> = {
    shape: 'circle',
    color: '#2E5CB8',
    orgName: 'HAZINA',
    topText: 'SAVINGS & CREDIT SOCIETY',
    bottomText: 'LIMITED',
    centerLine1: 'P.O.Box 9111',
    centerLine2: 'DAR ES SALAAM',
    stampImageUrl: undefined,
};

/**
 * Company information for deduction forms.
 */
export interface CompanyConfig {
    /** Full company name */
    name?: string;
    /** Company address */
    address?: string;
    /** Company phone number(s) */
    phone?: string;
    /** Company email address */
    email?: string;
    /** Company website URL */
    website?: string;
    /** P.O. Box */
    poBox?: string;
    /** Location/City */
    location?: string;
}

/**
 * Configurable text content for deduction forms.
 */
export interface DeductionFormTextContent {
    /** Reference number prefix (e.g. 'HSCS') */
    referencePrefix?: string;
    /** Subject line shown on the letter */
    subjectLine?: string;
    /** Body text descriptor (inserted into the body paragraph) */
    bodyText?: string;
    /** Title of the consent section */
    consentTitle?: string;
    /** Ordered list items for the member consent */
    consentItems?: string[];
    /** Footer/note at bottom of form */
    footerNote?: string;
    /** Title of the deduction form (shown in PDF header) */
    formTitle?: string;
    /** Authorization text used in the PDF form */
    authorizationText?: string;
    /** Left column signature label (e.g. "Tunashukuru kwa ushirikiano wako,") */
    senderThankYouText?: string;
    /** Left column signer title line 1 (e.g. "Ushirika Daima") */
    signerTitle1?: string;
    /** Left column signer title line 2 (e.g. "DISBURSEMENT OFFICER") */
    signerTitle2?: string;
    /** Right column receipt header (e.g. "Imepokelewa na kufanyiwa kazi na:") */
    receiverHeaderText?: string;
    /** Label for table section title (e.g. "LOANS ONN") */
    tableSectionTitle?: string;
    /** Consent intro text (the "Mimi, {name}, ninakubali na kuthibitisha kwamba:" part) */
    consentIntroText?: string;
    /** Member signature label in consent box */
    memberSignatureLabel?: string;
    /** Date label in consent box */
    dateLabelInConsent?: string;
}

export const DEFAULT_TEXT_CONTENT: Required<DeductionFormTextContent> = {
    referencePrefix: 'HSCS',
    subjectLine: 'KUWEKA MAKATO YA ',
    bodyText: 'ambae anatakiwa kuwekewa makato ya Mkopo wa {productName}',
    consentTitle: 'RIDHAA YA MWANACHAMA',
    consentItems: [
        'Nimesoma na kuelewa masharti ya ushirika',
        'Ninakubali makato ya kila mwezi kutoka mshahara wangu',
        'Ninakubali masharti yote ya uanachama',
        'Taarifa zote nilizotoa ni sahihi na za kweli',
    ],
    footerNote: 'Fomu hii ni muhimu kwa ajili ya uthibitisho wa makato ya kila mwezi. Hakikisha imetiwa sahihi na muhuri wa mwajiri.',
    formTitle: 'LOAN DEDUCTION FORM',
    authorizationText: 'hereby authorize my employer to deduct the monthly installment from my salary',
    senderThankYouText: 'Tunashukuru kwa ushirikiano wako,',
    signerTitle1: 'Ushirika Daima',
    signerTitle2: 'LOAN OFFICER',
    receiverHeaderText: 'Imepokelewa na kufanyiwa kazi na:',
    tableSectionTitle: 'LOANS ONN',
    consentIntroText: 'ninakubali na kuthibitisha kwamba:',
    memberSignatureLabel: 'Sahihi ya Mwanachama:',
    dateLabelInConsent: 'Tarehe:',
};

/**
 * Branding configuration for logo and watermark.
 */
export interface DeductionFormBrandingConfig {
    /**
     * URL or path to the organisation logo shown in the form header.
     * e.g. '/images/hazina-logo.png'
     */
    logoUrl?: string;
    /**
     * URL or path to a watermark image shown behind the form content.
     * Leave empty/undefined for no watermark.
     */
    watermarkUrl?: string;
    /** Whether to show the watermark. Defaults to false. */
    showWatermark?: boolean;
}

/**
 * Header configuration for deduction forms (company identity shown above the divider).
 */
export interface DeductionFormHeaderConfig {
    companyName?: string;
    companyAddress?: string;
    companyPhone?: string;
    companyEmail?: string;
    companyWebsite?: string;
}

/**
 * Combined configuration for deduction forms.
 */
export interface DeductionFormConfig {
    stamp?: StampConfig;
    textContent?: DeductionFormTextContent;
    /** Branding: logo URL, watermark URL/visibility */
    branding?: DeductionFormBrandingConfig;
    /** Header: company name and address shown below the logo */
    header?: DeductionFormHeaderConfig;
    /** Company: company information (name, address, phone, email, website, poBox, location) */
    company?: CompanyConfig;
}

/**
 * Explicit Hazina SACCOS deduction form configuration using the existing hard-coded stamp values.
 * Use this constant to set the Hazina form while the institution-configurable feature is not yet
 * fully wired end-to-end.
 */
export const HAZINA_DEDUCTION_FORM_CONFIG: DeductionFormConfig = {
    stamp: {
        ...DEFAULT_STAMP_CONFIG,
        stampImageUrl: '/images/hazina_stamp.svg',
    },
    textContent: DEFAULT_TEXT_CONTENT,
    branding: {
        logoUrl: '/images/hazina-logo.png',
        watermarkUrl: '/images/hazina-logo.png',
        showWatermark: true,
    },
};
